import React from 'react';
import {
  ChildrenItemType,
  OptionsItemType,
  RegionSelectValueType,
  SelectedMarketsInfoItemType,
  TSearchTagMode,
} from '@/pages/home/components/NewRegionSelect/type';
import { VALUE_TYPE_MARKET, VALUE_TYPE_REGION, LOCALE_CN } from '@/pages/home/components/NewRegionSelect/config';
import { uniq } from 'lodash-es';

// 根据markets 计算 region
export const getRegionValue = (markets: string[], option: OptionsItemType[], allChindren: ChildrenItemType[], radioValues: string[]): RegionSelectValueType => {
  markets = uniq(markets);
  if (markets.length < 1) {
    const firstRadio = radioValues?.[0];
    return {
      type: VALUE_TYPE_MARKET,
      regions: [],
      markets: firstRadio ? [firstRadio] : []
    };
  }
  const regions: string[] = [];
  let type = VALUE_TYPE_REGION;

  const marketsSet = new Set(markets);

  // 遍历组装map {region: ['xxx', 'fffff']}
  const regionMap = {} as any;
  allChindren.forEach(item => {
    const { parent, value } = item;
    if (marketsSet.has(value)) {
      if (!regionMap[parent]) {
        regionMap[parent] = [value];
      } else {
        regionMap[parent].push(value);
      }
    }
  });

  // 比较
  Object.keys(regionMap).map(key => {
    const region = option.find(item => item.value === key);
    const { disabled, children } = region || {};
    // if (region && region.children?.length && region.children.length === regionMap[key].length) {
    // 根据market判断是否全选（忽略disabled的market），联动勾选对应的region
    if (disabled && children?.length && children.length === regionMap[key].length) {
      regions.push(key);
    } else {
      const availableChildren =
        children?.filter((item) => !item?.disabled && !item?.isGroupHeader) ||
        [];
      if (availableChildren.length > 0 && availableChildren?.filter((item)=> !marketsSet.has(item.value)).length === 0) {
        regions.push(key);
      } else {
        type = VALUE_TYPE_MARKET;
      }
    }
  });
  return {
    type,
    regions,
    markets: Array.from(marketsSet)
  };
};

/** 双语标题 map（如 `selectedRegionName`） */
export type LocaleTextMap = {
  'en-US': string;
  'zh-CN': string;
};

/** 从双语文案 map 安全取当前 locale 文本；`locale` 为宽泛 string 时避免动态索引 TS 报错 */
export const getLocaleText = (
  textMap: LocaleTextMap | undefined,
  locale: string,
): string => {
  if (!textMap) {
    return '';
  }
  if (locale === LOCALE_CN) {
    return textMap['zh-CN'] || textMap['en-US'] || '';
  }
  return textMap['en-US'] || textMap['zh-CN'] || '';
};

export const getText = (item: SelectedMarketsInfoItemType | undefined, locale: string) => {
  const { value, text, textCN } = item || {};
  if (locale === LOCALE_CN) {
    return textCN || text || value;
  }
  return text || textCN || value;
};

export const getBubbleContentText = (
  item: SelectedMarketsInfoItemType | undefined,
  locale: string,
): string | React.ReactElement | undefined => {
  if (!item) {
    return undefined;
  }
  const { bubbleContent, bubbleContentCN } = item;
  return locale === LOCALE_CN ? bubbleContentCN : bubbleContent;
};

/**
 * 解析列表行 Bubble 内容（溢出模式专用）。
 *
 * 优先级：`bubbleContent` / `bubbleContentCN`（历史协议）> 溢出时 `tipText` > 无内容（不挂 Bubble）。
 * 纯函数，不改变 option 数据，可供左右列共用。
 *
 * @param item - 列表项，可含固定 `bubbleContent` / `bubbleContentCN`
 * @param locale - 当前语言环境（`zh-CN` / `en-US`）
 * @param isOverflow - 文本是否溢出（来自 `useIsTextOverflow`）
 * @param tipText - 溢出时展示的完整原文（非搜索高亮片段）
 * @returns Bubble 内容；`undefined` 时 `CommonBubble` 不挂载浮层
 */
export const resolveListBubbleContent = (
  item: SelectedMarketsInfoItemType | undefined,
  locale: string,
  isOverflow: boolean,
  tipText: string,
): string | React.ReactElement | undefined => {
  const customBubbleContent = getBubbleContentText(item, locale);
  return customBubbleContent || (isOverflow ? tipText : undefined);
};

/** 注入组头行的 value 前缀，不可进入 markets */
export const GROUP_HEADER_VALUE_PREFIX = '__group__';

/** 右侧 market 是否可勾选（排除注入的分组标题行） */
export const isSelectableMarketItem = (
  item?: ChildrenItemType,
): boolean => Boolean(item?.value) && !item?.isGroupHeader;

/** 二级选项是否命中搜索（含分组标题） */
export const isChildMatchSearch = (
  subItem: Pick<OptionsItemType, 'text' | 'textCN' | 'groupText' | 'groupTextCN'>,
  searchStr: string,
): boolean => {
  const textHit =
    subItem.text?.toLowerCase().includes(searchStr) ||
    subItem.textCN?.toLowerCase().includes(searchStr);
  const groupHit =
    subItem.groupText?.toLowerCase().includes(searchStr) ||
    subItem.groupTextCN?.toLowerCase().includes(searchStr);
  return Boolean(textHit || groupHit);
};

/**
 * 在右侧 market 列表按 groupKey 注入组头行。
 * 组头仅展示层插入，disabled 且不参与全选/勾选。
 */
export const injectGroupHeaders = (
  list: ChildrenItemType[],
): ChildrenItemType[] => {
  if (!list?.length) {
    return [];
  }
  const result: ChildrenItemType[] = [];
  let lastGroupKey: string | undefined;

  list.forEach((item) => {
    if (item.isGroupHeader) {
      result.push(item);
      return;
    }
    const groupKey = item.groupKey?.trim();
    if (groupKey && groupKey !== lastGroupKey) {
      // 组头 icon/render 取自组内首项；groupHeaderShowIcon 默认真，仅 false 时不注入 icon
      result.push({
        value: `${GROUP_HEADER_VALUE_PREFIX}${item.parent}__${groupKey}`,
        text: item.groupText || groupKey,
        textCN: item.groupTextCN || item.groupText || groupKey,
        groupKey,
        groupText: item.groupText || groupKey,
        groupTextCN: item.groupTextCN || item.groupText || groupKey,
        parent: item.parent,
        parentText: item.parentText,
        parentTextCN: item.parentTextCN,
        disabled: true,
        isGroupHeader: true,
        render: item.render,
        ...(item.groupHeaderShowIcon !== false && {
          icon: item.icon,
          svgIconType: item.groupHeaderSvgIconType || item.svgIconType,
        }),
      });
      lastGroupKey = groupKey;
    } else if (!groupKey) {
      lastGroupKey = undefined;
    }
    result.push(item);
  });
  return result;
};

/** 搜索栏 region 模式下，将已选 regions 转为 tag 项 */
export const buildRegionTagItems = (
  regions: string[],
  options: OptionsItemType[],
): SelectedMarketsInfoItemType[] => {
  const items: SelectedMarketsInfoItemType[] = [];
  regions.forEach((regionValue) => {
    const regionOption = options.find((option) => option.value === regionValue);
    if (regionOption) {
      items.push({
        value: regionOption.value,
        text: regionOption.text || '',
        textCN: regionOption.textCN || regionOption.text || '',
      });
    }
  });
  return items;
};

/** 搜索栏已选 tag 列表（market / region 模式） */
export const buildSelectedMarketsInfo = (params: {
  markets: string[];
  regions: string[];
  searchTagMode: TSearchTagMode;
  options: OptionsItemType[];
  allChildrenMap: Record<string, ChildrenItemType>;
  radioValues: string[];
}): SelectedMarketsInfoItemType[] => {
  const {
    markets,
    regions,
    searchTagMode,
    options,
    allChildrenMap,
    radioValues,
  } = params;
  const isRegionTagMode = searchTagMode === 'region';
  const regionSet = isRegionTagMode ? new Set(regions) : null;
  // 性能优化：radioValues 转 Set，forEach 中 has() 为 O(1)
  const radioValuesSet = new Set(radioValues);
  const selectedMarketsInfo: SelectedMarketsInfoItemType[] = isRegionTagMode
    ? buildRegionTagItems(regions, options)
    : [];

  markets.forEach((val) => {
    if (radioValuesSet.has(val)) {
      return;
    }
    const find = allChildrenMap?.[val];
    // 先判断 find 是否存在，再访问其属性，避免 undefined.parent 报错
    if (!find) {
      selectedMarketsInfo.push({
        value: val,
        text: val,
        textCN: val,
      });
      return;
    }
    if (isRegionTagMode && regionSet?.has(find.parent)) {
      return;
    }
    selectedMarketsInfo.push(find);
  });
  return selectedMarketsInfo;
};

/** 关闭搜索栏 tag 后剩余的 markets */
export const resolveMarketsAfterCloseTag = (
  markets: string[],
  closedValue: string,
  searchTagMode: TSearchTagMode,
  options: OptionsItemType[],
): string[] => {
  if (searchTagMode !== 'region') {
    return markets.filter((val) => closedValue !== val);
  }
  const regionOption = options.find((option) => option.value === closedValue);
  if (!regionOption) {
    return markets.filter((val) => closedValue !== val);
  }
  const childValueSet = new Set(
    (regionOption.children || [])
      .filter((child) => !child?.disabled)
      .map((child) => child.value),
  );
  return markets.filter((val) => !childValueSet.has(val));
};